import type { Config } from 'tailwindcss';
import colors from 'tailwindcss/colors';

export default {
  darkMode: 'class',
  content: ['./src/**/*.{js,jsx,ts,tsx}'],
  theme: {
    colors: {
      grey: colors.gray,
      slate: colors.slate,
    },
    extend: {
      backgroundImage: {
        // Aurora accent gradient (production default = orange)
        grad: 'linear-gradient(135deg, #ff7a45, #ef4823)',
        'grad-dark': 'linear-gradient(135deg, #ff8a5b, #f5512c)',
        // Store page background: a five-stop pastel mesh, blurred behind the content
        mesh: 'radial-gradient(1150px 860px at -2% -2%, rgba(255,164,108,.85), transparent 56%), radial-gradient(1100px 820px at 102% -2%, rgba(140,226,162,.78), transparent 54%), radial-gradient(1200px 960px at 18% 66%, rgba(214,160,242,.74), transparent 56%), radial-gradient(1300px 980px at 86% 104%, rgba(138,180,255,.88), transparent 56%), radial-gradient(980px 800px at 56% 30%, rgba(255,168,196,.62), transparent 55%), linear-gradient(180deg, #eceef5, #e6eaf3)',
        'mesh-dark':
          'radial-gradient(1150px 860px at -2% -2%, rgba(255,150,95,.3), transparent 56%), radial-gradient(1100px 820px at 102% -2%, rgba(110,210,150,.24), transparent 54%), radial-gradient(1200px 960px at 18% 66%, rgba(184,130,228,.28), transparent 56%), radial-gradient(1300px 980px at 86% 104%, rgba(110,150,248,.3), transparent 56%), linear-gradient(180deg, #191623, #13111b)',
        headerbg:
          'linear-gradient(110deg, hsla(26.7,74%,96.7%,.68), hsla(216,66%,98%,.36) 26%, hsla(280,34%,98%,.82) 78%, hsla(210,45%,97.5%,.86))',
        'headerbg-dark':
          'linear-gradient(110deg, hsla(26.7,60%,40%,.3), hsla(216,50%,46%,.12) 26%, hsla(280,32%,44%,.26) 78%, hsla(210,40%,44%,.28))',
        'image-well':
          'radial-gradient(120% 90% at 50% 8%, rgba(241,90,43,.12), transparent 62%)',
        'image-well-dark':
          'radial-gradient(120% 90% at 50% 8%, rgba(255,122,77,.12), transparent 62%)',
      },
      fontFamily: {
        sans: ['Inter', 'ui-sans-serif', 'system-ui', 'sans-serif'],
        mono: ['JetBrains Mono', 'ui-monospace', 'SFMono-Regular', 'monospace'],
      },
      borderRadius: {
        lg: '0.5rem',
        xl: '0.75rem',
        '2xl': '1rem',
        '3xl': '1.5rem',
      },
      boxShadow: {
        card: '0px 4px 16px hsla(0, 0%, 0%, 0.04)',
        'pop-up': '0px 4px 10px 0px hsla(0,0%,0%,0.15)',
        dialog: '0px 6px 15px 0px hsla(0,0%,0%,0.2)',
        sidebar: '0px 4px 20px 0 hsla(0,0%,0%,10%)',
        header: '0px 1px 8px 0 hsla(0,0%,0%,10%)',
        orange: '0px 4px 12px hsla(16, 100%, 61%, 0.4)',
        'orange-card': '0px 6px 12px hsla(16, 100%, 71%, 0.25)',
        'orange-chip': '0px 3px 8px hsla(16 100% 70% / 0.5)',
        // Aurora frosted-glass overlay, card lift, and list-row hover
        glass:
          '0 32px 84px -28px rgba(70,60,90,.32), 0 2px 10px rgba(70,60,90,.06)',
        'glass-dark':
          '0 36px 90px -28px rgba(0,0,0,.7), 0 2px 10px rgba(0,0,0,.45)',
        'aurora-card':
          '0 1px 2px rgba(40,30,30,.04), 0 14px 34px -16px rgba(60,50,60,.12)',
        'aurora-card-dark':
          '0 1px 2px rgba(0,0,0,.35), 0 16px 40px -18px rgba(0,0,0,.5)',
        'row-hover': '0 12px 26px -16px rgba(241,90,43,.34)',
        // Store header glass, focus rings, selection ring, and product imagery
        'store-header': '0 2px 12px hsla(215, 22%, 64%, .3)',
        'store-header-dark': '0 3px 12px rgba(0,0,0,.4)',
        ring: '0 0 0 3px rgba(241,90,43,.42)',
        'ring-dark': '0 0 0 3px rgba(0,0,0,.5)',
        tile: '0 0 0 3px rgba(241,90,43,.16)',
        'tile-dark': '0 0 0 3px rgba(255,122,77,.16)',
        bottle: '0 26px 44px -18px rgba(20,14,30,.5)',
        'step-current': '0 6px 14px -5px rgba(241,90,43,.55)',
      },
      transitionProperty: {
        width: 'width',
      },
      animation: {
        spinner: 'spinner 0.6s linear infinite',
      },
      colors: {
        transparent: 'transparent',
        white: '#fff',
        black: '#1b2932',
        'site-background': '#fafafb',
        'dialog-backdrop': '#15181C',
        brand: {
          blue: '#002255',
          orange: '#ff9068',
          // Aurora accent (production default = orange)
          accent: '#f15a2b',
          'accent-dark': '#ff7a4d',
        },
        blue: {
          1: '#E1EFF9',
          2: '#10a2fd',
          3: '#357dd2',
          4: '#255fb0',
          ...colors.blue,
        },
        cyan: {
          1: '#DBF5F7',
          2: '#22d3ee',
          3: '#06b6d4',
          4: '#0e7490',
        },
        emerald: {
          1: '#E4F9F0',
          2: '#6ee7b7',
          3: '#34d399',
          4: '#059669',
        },
        green: {
          1: '#b4f3cf',
          2: '#58bd85',
          3: '#4aa37f',
          4: '#3a8474',
        },
        lime: {
          1: '#d9f99d',
          2: '#bef264',
          3: '#a3e635',
          4: '#84cc16',
        },
        navy: {
          1: '#6c8fe0',
          2: '#234aa4',
          3: '#1a377a',
          4: '#0c2254',
        },
        orange: {
          1: '#ffd1b7',
          2: '#f97b19',
          3: '#ff6b36',
          4: '#cc4700',
          ...colors.orange,
        },
        pink: {
          1: '#FCE2E5',
          2: '#fb7195',
          3: '#f43f5e',
          4: '#e11d48',
        },
        purple: {
          1: '#869af4',
          2: '#5370f4',
          3: '#475ece',
          4: '#3449ae',
          ...colors.purple,
        },
        red: {
          1: '#feb3af',
          2: '#f2625a',
          3: '#df2a20',
          4: '#bd180f',
        },
        yellow: {
          1: '#fef08a',
          2: '#fde047',
          3: '#facc15',
          4: '#eab308',
        },
        grey: {
          1: '#fafafa',
          2: '#f3f5f7',
          3: '#e2e6e9',
          4: '#cedfe8',
          5: '#a7c1d2',
          6: '#7e9eb5',
          7: '#496f88',
          8: '#2d4453',
          9: '#121b21',
        },
        // Aurora warm text neutrals (light-mode inks), lightest -> darkest.
        // Primary text = taupe-3, secondary = taupe-2, tertiary/muted = taupe-1.
        taupe: {
          1: '#938b80',
          2: '#5f5a52',
          3: '#1a1510',
        },
        // Aurora cool text neutrals (dark-mode inks), lightest -> darkest.
        // Dark primary = mauve-1, secondary = mauve-2, tertiary/muted = mauve-3.
        mauve: {
          1: '#f3eff8',
          2: '#bfb6cb',
          3: '#8b8299',
        },
        // Store glass surfaces. Each pairs a light value with its `-dark` twin,
        // selected in class strings via the `dark:` variant.
        panel: 'rgba(255,255,255,.82)',
        'panel-dark': 'rgba(51,46,67,.84)',
        panel2: 'rgba(120,110,100,.07)',
        'panel2-dark': 'rgba(255,255,255,.07)',
        over: 'rgba(255,255,255,.86)',
        'over-dark': 'rgba(33,29,47,.82)',
        frame: 'rgba(255,255,255,.5)',
        'frame-dark': 'rgba(36,32,50,.56)',
        'frame-border': 'rgba(255,255,255,.72)',
        'frame-border-dark': 'rgba(255,255,255,.14)',
        hair: 'rgba(70,60,50,.1)',
        'hair-dark': 'rgba(255,255,255,.12)',
        hair2: 'rgba(70,60,50,.055)',
        'hair2-dark': 'rgba(255,255,255,.06)',
        // Positive/in-stock accent
        pos: '#159f6a',
        'pos-dark': '#34d399',
        visa: '#1a1f71',
      },
    },
  },
  plugins: [],
} satisfies Config;
